import { companyName } from 'config'
import { removeNonPagePermissions } from 'hooks/hasPagePermissions'
import { usePermissions } from 'hooks/usePermissions'
import Parties from 'pages/Loan/Parties'
import { useEffect, useMemo, useState } from 'react'
import { useHistory } from 'react-router-dom'
import { PaymentOrderType } from 'services'
import { useTitle } from 'utils/pageTitle'

import { AiPromptTemplates } from './AiPromptTemplates'
import { ApplicationEmailTemplate } from './ApplicationEmailTemplate'
import { BrokerAgreement } from './BrokerAgreement'
import { BrokerConditionCategories } from './BrokerConditionCategories'
import { BrokerEmails } from './BrokerEmails'
import { BrokerInvitationEmails } from './BrokerInvitationEmails'
import { BrokerTemplates } from './BrokerTemplates'
import { BrokerVerificationCategory } from './BrokerVerificationCategory/BrokerVerificationCategory'
import { ClosingCostTemplate } from './ClosingCostTemplate'
import { Conditions } from './Conditions'
import { EmailTemplates } from './EmailTemplates'
import { EscrowHoldbackAgreementTemplate } from './EscrowHoldbackAgreement'
import { InvestorsServicers } from './InvestorsServicers'
import { LoanStructureConditions } from './LoanStructureConditions'
import { LoanTermSheet } from './LoanTermSheet'
import { MortgageClauseTemplates } from './MortgageClauseTemplates'
import { Notes } from './Notes'
import { PaymentTypes } from './PaymentType'
import { ReminderTemplates } from './ReminderTemplates'
import { ReviewFormItems } from './ReviewFormItems'
import { Rolodex } from './Rolodex'
import SurveyTemplates from './SurveyTemplates'
import { Tasks } from './Tasks'
import { UnderwriterTemplates } from './Templates'
import { UWConditionCategories } from './UWConditionCategories'
import { WarehouseLenders } from './WarehouseLenders'
import { WireInstructionTemplates } from './WireInstructionTemplates'

type menuType = {
  [key: string]: string //fix this
}

const _leftMenuItems: menuType = {
  conditions: 'Conditions',
  underwriterTemplates: 'UW Condition Templates',
  uwConditionCategories: 'UW Condition Categories',
  brokerTemplates: 'Loan Setup Condition Templates',
  brokerConditionCategories: 'Loan Setup Condition Categories',
  loanStructureConditions: 'Loan Structure & Application Conditions',
  party: 'Parties',
  tasks: 'Tasks',
  reminderTemplates: 'Reminder Templates',
  notes: 'Notes',
  wireInstructionTemplates: 'Wire Instruction Templates',
  rolodex: 'Rolodex',
  reviewForm: 'Valuation Review Template',
  investorsServicers: 'Investors & Servicers',
  emailTemplates: `Email Templates`,
  aiPromptTemplates: `AI Prompt Templates`,
  loanTermSheet: `Loan Term Sheet / Pre-Approval Template`,
  brokerAgreement: 'Third Party Agreement Template',
  escrowHoldbackAgreement: 'Escrow Holdback Agreement Template',
  brokerEmails: 'Third Party Email Template',
  brokerInvitationEmails: 'Broker Invitation Email Template',
  warehouseLender: 'Warehouse Lenders',
  invoicePaymentTypes: 'Invoice Fee Templates',
  surveyTemplate: 'Review Templates',
  applicationEmailTemplate: 'Loan Application Email Template',
  thridPartyApprovalCategory: 'Third party Approval Categories',
  closingCostTemplate: 'Closing Cost Template',
  mortgageClauseTemplate: 'Mortgagee Clause Template',
}

export default function ConditionsAndTemplates() {
  useTitle(`Conditions and Templates - ${companyName}`)

  const { hasPermission } = usePermissions()

  const navigate = useHistory()
  const [selectedMenu, setSelectedMenu] = useState('')

  const setMenu = (menuItem: string) => {
    setSelectedMenu(menuItem)
    navigate.push(`/condition_template/${menuItem}`)
  }

  const renderFragment = useMemo(() => {
    switch (selectedMenu) {
      case 'conditions':
        return <Conditions />
      case 'underwriterTemplates':
        return <UnderwriterTemplates />
      case 'uwConditionCategories':
        return <UWConditionCategories />
      case 'brokerTemplates':
        return <BrokerTemplates />
      case 'brokerConditionCategories':
        return <BrokerConditionCategories />
      case 'loanStructureConditions':
        return <LoanStructureConditions />
      case 'tasks':
        return <Tasks />
      case 'reminderTemplates':
        return <ReminderTemplates />
      case 'notes':
        return <Notes />
      case 'wireInstructionTemplates':
        return <WireInstructionTemplates />
      case 'rolodex':
        return <Rolodex />
      case 'reviewForm':
        return <ReviewFormItems />
      case 'investorsServicers':
        return <InvestorsServicers />
      case 'emailTemplates':
        return <EmailTemplates />
      case 'aiPromptTemplates':
        return <AiPromptTemplates />
      case 'loanTermSheet':
        return <LoanTermSheet />
      case 'invoicePaymentTypes':
        return <PaymentTypes type={PaymentOrderType.LoanSubmissionRush} />
      case 'surveyTemplate':
        return <SurveyTemplates />
      case 'brokerAgreement':
        return <BrokerAgreement />
      case 'escrowHoldbackAgreement':
        return <EscrowHoldbackAgreementTemplate />
      case 'brokerEmails':
        return <BrokerEmails />
      case 'warehouseLender':
        return <WarehouseLenders />
      case 'party':
        return <Parties />
      case 'applicationEmailTemplate':
        return <ApplicationEmailTemplate />
      case 'brokerInvitationEmails':
        return <BrokerInvitationEmails />
      case 'thridPartyApprovalCategory':
        return <BrokerVerificationCategory />
      case 'closingCostTemplate':
        return <ClosingCostTemplate />
      case 'mortgageClauseTemplate':
        return <MortgageClauseTemplates />
      default: {
        return <div>Comming Soon...</div>
      }
    }
  }, [selectedMenu])

  const leftMenuItems = useMemo(() => {
    removeNonPagePermissions(_leftMenuItems as any, ['condition_template'])
    if (!hasPermission('Template.ClosingCost')) {
      delete _leftMenuItems.closingCostTemplate
    }
    return _leftMenuItems
  }, [])

  useEffect(() => {
    setMenu(Object.keys(leftMenuItems)[0])
  }, [])

  return (
    <div className="vendors-container py-6">
      <div className="vendors-wrapper max-w-screen-2xl m-auto grid grid-cols-12 gap-6 px-2">
        <div className="sidebar-left col-span-12 md:col-span-3 shrink-0 bg-white shadow1 rounded">
          <ul className="sidebar-items flex flex-col p-4 pb-20">
            {Object.keys(leftMenuItems).map((item: string, index) => {
              return (
                <li
                  key={index}
                  onClick={() => {
                    setMenu(item)
                  }}
                  className="border-b py-2"
                >
                  <p
                    className={`hover:underline cursor-pointer ${
                      selectedMenu === item ? 'border px-4 py-1 bg-zinc-100' : 'py-1'
                    }`}
                  >
                    {index + 1}. {leftMenuItems[item as keyof typeof leftMenuItems]}
                  </p>
                </li>
              )
            })}
          </ul>
        </div>
        <div className="content-right col-span-12 md:col-span-9 bg-white p-4 rounded shadow1">{renderFragment}</div>
      </div>
    </div>
  )
}
